import { ChevronRight } from "lucide-react"

import { getOS } from "@/shared/lib/native-browser"

import heroBg from "../assets/general-bg.webp"
import logoOrb from "../assets/signin-logo.webp"
import { AppleIcon, GoogleIcon } from "./brand-icons"
import { SignInErrorToast } from "./sign-in-error-toast"

interface MainSignInProps {
  onApple: () => void
  onGoogle: () => void
  /** "Take a short quiz" — for first-time visitors. */
  onQuiz: () => void
  /** The last sign-in attempt failed — show the "Can't log in" toast on top. */
  error?: boolean
  /** Clear the error toast (also fired by its own 4s auto-dismiss). */
  onDismissError?: () => void
}

/**
 * Primary sign-in screen (route `/signin`) for returning users. New visitors
 * are funnelled into the quiz. Presentational — the route wires the handlers.
 */
export function MainSignIn({ onApple, onGoogle, onQuiz, error, onDismissError }: MainSignInProps) {
  // Apple ID is virtually unused on Android — hide that button there (Google only).
  const isAndroid = getOS() === "android"

  return (
    <div
      data-slot="main-sign-in"
      className="relative flex min-h-[100dvh] flex-col items-center justify-between overflow-hidden bg-dark-base px-4 pb-4 pt-10 text-purple-light"
      style={{ fontFamily: '"Red Hat Text Variable", sans-serif' }}
    >
      <img
        src={heroBg}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 h-60 w-full max-w-app -translate-x-1/2 object-cover"
      />

      {error && onDismissError && (
        <div className="absolute inset-x-0 top-4 z-20 flex justify-center px-4">
          <div className="w-full max-w-app">
            <SignInErrorToast onDismiss={onDismissError} />
          </div>
        </div>
      )}

      <div className="relative z-10 flex w-full max-w-app flex-1 flex-col items-center gap-6">
        <img src={logoOrb} alt="Live Palmistry" className="size-[120px] rounded-[30px] object-cover" />

        <div className="flex flex-col gap-2 text-center">
          <h1 className="font-display text-2xl font-medium leading-tight text-purple-light">
            Welcome to Live Palmistry
          </h1>
          <p className="text-base leading-snug text-purple-light/70">
            Sign in to access your personal readings, daily guidance, astrology tools, and saved
            reports anytime
          </p>
        </div>

        <div className="flex w-full flex-col gap-1">
          {!isAndroid && (
            <>
              <button
                type="button"
                onClick={onApple}
                className="flex h-14 w-full items-center justify-center gap-1 rounded-[26px] bg-purple-light/10 text-base font-semibold text-white transition-colors hover:bg-purple-light/15"
              >
                <AppleIcon className="size-8 fill-white" />
                Sign in with Apple
              </button>

              <div className="flex items-center gap-4 px-4 py-2">
                <span className="h-px flex-1 bg-purple-light/20" />
                <span className="text-base text-purple-light/40">or</span>
                <span className="h-px flex-1 bg-purple-light/20" />
              </div>
            </>
          )}

          <button
            type="button"
            onClick={onGoogle}
            className="flex h-14 w-full items-center justify-center gap-1 rounded-[26px] bg-purple-light/10 text-base font-semibold text-white transition-colors hover:bg-purple-light/15"
          >
            <GoogleIcon className="size-8" />
            Sign in with Google
          </button>
        </div>
      </div>

      <div className="relative z-10 flex w-full max-w-app items-center gap-4 border-t border-purple-light/20 pt-4">
        <div className="flex flex-1 flex-col gap-0.5">
          <p className="text-sm font-semibold text-white">First time here?</p>
          <p className="text-xs text-purple-light/70">
            Take a short quiz and create your personal guidance profile in minutes
          </p>
        </div>
        <button
          type="button"
          onClick={onQuiz}
          className="flex h-11 shrink-0 items-center gap-2 rounded-full bg-gradient-to-b from-peach to-purple-accent px-5 text-sm font-semibold text-white"
        >
          Take a short quiz
          <ChevronRight className="size-3.5" />
        </button>
      </div>
    </div>
  )
}
